import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Target, Users, Calendar, FileText, TrendingUp, 
  ChevronRight, ChevronLeft, Edit2, Save, X, AlertCircle 
} from 'lucide-react';

import { API_URL } from '../../utils/apiUrl';
const PAGE_SIZE = 5; // Show 5 clients per page

const formatCurrency = (value) => {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${value?.toLocaleString('en-IN') || 0}`;
};

const PriorityBadge = ({ priority }) => {
  const colors = {
    high: 'bg-red-500/20 text-red-400 border-red-500/30',
    medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    low: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
  };
  
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${colors[priority] || colors.low}`}>
      {priority}
    </span>
  );
};

const LikelihoodBadge = ({ likelihood }) => {
  if (!likelihood) return <span className="text-xs text-slate-500">Not set</span>;
  
  const colors = {
    high: 'text-emerald-400',
    medium: 'text-amber-400',
    low: 'text-red-400'
  };
  
  return (
    <span className={`text-xs capitalize ${colors[likelihood] || 'text-slate-400'}`}>
      {likelihood}
    </span>
  );
};

// =====================================================
// DEALER/RM FOCUS CLIENTS WIDGET (with Pagination)
// =====================================================
export const DealerFocusClientsWidget = ({ token }) => {
  const [allClients, setAllClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    proposed_meeting_date: '',
    next_followup_date: '',
    discussion_notes: '',
    activation_likelihood: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Calculate pagination
  const totalClients = allClients.length;
  const totalPages = Math.ceil(totalClients / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const currentClients = allClients.slice(startIndex, endIndex);
  
  // Calculate rank range for display
  const rankStart = startIndex + 1;
  const rankEnd = Math.min(endIndex, totalClients);

  const fetchFocusClients = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch more clients (up to 100) to support pagination
      const res = await axios.get(`${API_URL}/focus-clients?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllClients(res.data.focus_clients || []);
    } catch (err) {
      console.error('Failed to fetch focus clients:', err);
      setError('Failed to load focus clients');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchFocusClients();
  }, [fetchFocusClients]);

  const startEditing = (client) => {
    setEditingClient(client.client_id);
    setFormData({
      proposed_meeting_date: client.proposed_meeting_date || '',
      next_followup_date: client.next_followup_date || '',
      discussion_notes: client.discussion_notes || '',
      activation_likelihood: client.activation_likelihood || ''
    });
  };

  const cancelEditing = () => {
    setEditingClient(null);
    setFormData({
      proposed_meeting_date: '',
      next_followup_date: '',
      discussion_notes: '',
      activation_likelihood: ''
    });
  };

  const savePlan = async (clientId) => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/focus-clients/${clientId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingClient(null);
      fetchFocusClients();
    } catch (err) {
      console.error('Failed to save plan:', err);
      setError('Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) {
    return (
      <Card className="card-vault" data-testid="focus-clients-dealer-widget">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-vault" data-testid="focus-clients-dealer-widget">
      <CardHeader className="border-b border-white/5">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-[#C5A059]" />
            Focus Clients - Brokerage Activation
          </CardTitle>
          {totalClients > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">
                Showing #{rankStart}-{rankEnd} of {totalClients} (by AUM)
              </span>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-slate-400 min-w-[40px] text-center">
                  {currentPage}/{totalPages || 1}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}
        
        {allClients.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No focus clients assigned</p>
            <p className="text-xs mt-1">Clients will appear here based on AUM ranking</p>
          </div>
        ) : (
          <div className="space-y-3">
            {currentClients.map((client, idx) => (
              <div 
                key={client.client_id} 
                className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-[#C5A059]/30 transition-colors"
              >
                {/* Client Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-slate-500 font-mono">#{startIndex + idx + 1}</span>
                      <span className="font-medium text-white">{client.client_name}</span>
                      <PriorityBadge priority={client.priority_category} />
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <span className="font-mono">{client.client_code}</span>
                      <span>•</span>
                      <span className="text-[#C5A059] font-medium">{formatCurrency(client.current_aum)}</span>
                    </div>
                  </div>
                  
                  {editingClient !== client.client_id && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => startEditing(client)}
                      className="text-slate-400 hover:text-[#C5A059]"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                {/* Edit Mode */}
                {editingClient === client.client_id ? (
                  <div className="space-y-3 pt-3 border-t border-white/10">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Meeting Date</label>
                        <Input
                          type="date"
                          value={formData.proposed_meeting_date}
                          onChange={(e) => setFormData({...formData, proposed_meeting_date: e.target.value})}
                          className="bg-white/5 border-white/10 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Follow-up Date</label>
                        <Input
                          type="date"
                          value={formData.next_followup_date}
                          onChange={(e) => setFormData({...formData, next_followup_date: e.target.value})}
                          className="bg-white/5 border-white/10 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Activation Likelihood</label>
                      <select
                        value={formData.activation_likelihood}
                        onChange={(e) => setFormData({...formData, activation_likelihood: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#C5A059] outline-none"
                      >
                        <option value="">Select...</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Discussion Notes</label>
                      <Textarea
                        value={formData.discussion_notes}
                        onChange={(e) => setFormData({...formData, discussion_notes: e.target.value})}
                        placeholder="Add notes about client engagement..."
                        className="bg-white/5 border-white/10 text-sm min-h-[80px]"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={cancelEditing} disabled={saving}>
                        <X className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                      <Button size="sm" onClick={() => savePlan(client.client_id)} disabled={saving}
                        className="bg-[#C5A059] hover:bg-[#B8934F] text-black"
                      >
                        {saving ? (
                          <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full mr-1" />
                        ) : (
                          <Save className="w-4 h-4 mr-1" />
                        )}
                        Save Plan
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* View Mode - Planning Summary */
                  <div className="grid grid-cols-3 gap-4 pt-3 border-t border-white/10 text-sm">
                    <div>
                      <div className="text-slate-500 text-xs mb-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Meeting
                      </div>
                      <div className="text-slate-300">
                        {client.proposed_meeting_date || <span className="text-slate-600">Not scheduled</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500 text-xs mb-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Follow-up
                      </div>
                      <div className="text-slate-300">
                        {client.next_followup_date || <span className="text-slate-600">Not set</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500 text-xs mb-1 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> Likelihood
                      </div>
                      <LikelihoodBadge likelihood={client.activation_likelihood} />
                    </div>
                    {client.discussion_notes && (
                      <div className="col-span-3">
                        <div className="text-slate-500 text-xs mb-1 flex items-center gap-1">
                          <FileText className="w-3 h-3" /> Notes
                        </div>
                        <div className="text-slate-400 text-xs line-clamp-2">{client.discussion_notes}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              className="text-slate-400"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous 5
            </Button>
            <span className="text-xs text-slate-500">
              Page {currentPage} of {totalPages}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={goToNextPage}
              disabled={currentPage >= totalPages}
              className="text-slate-400"
            >
              Next 5 <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =====================================================
// PRINCIPAL FOCUS CLIENTS OVERVIEW WIDGET (with Pagination)
// =====================================================
export const PrincipalFocusClientsWidget = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [dealerClients, setDealerClients] = useState([]);
  const [loadingDrilldown, setLoadingDrilldown] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination for dealer drilldown
  const [currentPage, setCurrentPage] = useState(1);
  const totalDealerClients = dealerClients.length;
  const totalPages = Math.ceil(totalDealerClients / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const currentDealerClients = dealerClients.slice(startIndex, endIndex);

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/focus-clients/principal-overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(res.data);
    } catch (err) {
      console.error('Failed to fetch overview:', err);
      setError('Failed to load focus clients overview');
    } finally {
      setLoading(false);
    }
  };

  const drilldownToDealer = async (dealerId, dealerName) => {
    try {
      setLoadingDrilldown(true);
      setSelectedDealer({ id: dealerId, name: dealerName });
      setCurrentPage(1); // Reset pagination
      const res = await axios.get(`${API_URL}/focus-clients/dealer/${dealerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDealerClients(res.data.clients || []);
    } catch (err) {
      console.error('Failed to fetch dealer clients:', err);
      setError('Failed to load dealer focus clients');
    } finally {
      setLoadingDrilldown(false);
    }
  };

  const backToOverview = () => {
    setSelectedDealer(null);
    setDealerClients([]);
    setCurrentPage(1);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) {
    return (
      <Card className="card-vault" data-testid="focus-clients-principal-widget">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  // Drilldown View - Dealer's Focus Clients (with Pagination)
  if (selectedDealer) {
    const rankStart = startIndex + 1;
    const rankEnd = Math.min(endIndex, totalDealerClients);
    
    return (
      <Card className="card-vault" data-testid="focus-clients-principal-widget">
        <CardHeader className="border-b border-white/5">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#C5A059]" />
              {selectedDealer.name}&apos;s Focus Clients
            </CardTitle>
            <div className="flex items-center gap-3">
              {totalDealerClients > 0 && (
                <span className="text-xs text-slate-400">
                  #{rankStart}-{rankEnd} of {totalDealerClients}
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={backToOverview} className="text-slate-400 hover:text-white">
                ← Back
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loadingDrilldown ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full" />
            </div>
          ) : dealerClients.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No focus clients for this dealer</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {currentDealerClients.map((client, idx) => (
                  <div 
                    key={client.client_id}
                    className="p-3 rounded-lg bg-white/5 border border-white/10"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 font-mono w-6">#{startIndex + idx + 1}</span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{client.client_name}</span>
                            <PriorityBadge priority={client.priority_category} />
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {client.client_code} • {formatCurrency(client.current_aum)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="text-xs text-slate-500">Meeting</div>
                            <div className="text-slate-300">{client.proposed_meeting_date || '-'}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500">Likelihood</div>
                            <LikelihoodBadge likelihood={client.activation_likelihood} />
                          </div>
                          {client.has_notes && (
                            <div className="text-xs text-amber-500" title="Has discussion notes">
                              <FileText className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination for Drilldown */}
              {totalPages > 1 && (
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={goToPrevPage}
                    disabled={currentPage === 1}
                    className="text-slate-400"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Prev 5
                  </Button>
                  <span className="text-xs text-slate-500">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={goToNextPage}
                    disabled={currentPage >= totalPages}
                    className="text-slate-400"
                  >
                    Next 5 <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // Main Overview View
  return (
    <Card className="card-vault" data-testid="focus-clients-principal-widget">
      <CardHeader className="border-b border-white/5">
        <CardTitle className="font-heading text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-[#C5A059]" />
          Brokerage Activation Focus - Firm Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}
        
        {/* Priority Distribution Summary */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-center">
            <div className="text-2xl font-bold text-[#C5A059]">
              {overview?.firm_overview?.total_focus_clients || 0}
            </div>
            <div className="text-xs text-slate-400">Total Clients</div>
          </div>
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
            <div className="text-2xl font-bold text-red-400">
              {overview?.firm_overview?.priority_distribution?.high || 0}
            </div>
            <div className="text-xs text-slate-400">High Priority</div>
          </div>
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
            <div className="text-2xl font-bold text-amber-400">
              {overview?.firm_overview?.priority_distribution?.medium || 0}
            </div>
            <div className="text-xs text-slate-400">Medium Priority</div>
          </div>
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
            <div className="text-2xl font-bold text-emerald-400">
              {overview?.firm_overview?.priority_distribution?.low || 0}
            </div>
            <div className="text-xs text-slate-400">Low Priority</div>
          </div>
        </div>

        {/* Dealer Summary with Drilldown */}
        <div className="space-y-2">
          <div className="text-sm text-slate-400 mb-3">Dealer-wise Distribution (Click to view Top 5 clients)</div>
          {overview?.dealer_summary?.length === 0 ? (
            <div className="text-center py-4 text-slate-500 text-sm">
              No dealer assignments found
            </div>
          ) : (
            overview?.dealer_summary?.map((dealer) => (
              <div 
                key={dealer.dealer_id}
                onClick={() => drilldownToDealer(dealer.dealer_id, dealer.dealer_name)}
                className="p-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#C5A059]/30 cursor-pointer transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                      <Users className="w-4 h-4 text-[#C5A059]" />
                    </div>
                    <div>
                      <div className="font-medium text-white">{dealer.dealer_name}</div>
                      <div className="text-xs text-slate-400">
                        {dealer.focus_client_count} clients • {formatCurrency(dealer.total_aum)} AUM
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex gap-2">
                      <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">
                        {dealer.priority_split?.high || 0} H
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">
                        {dealer.priority_split?.medium || 0} M
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
                        {dealer.priority_split?.low || 0} L
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-[#C5A059] transition-colors" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default { DealerFocusClientsWidget, PrincipalFocusClientsWidget };
